import React, { useState, useEffect } from 'react';
import { Briefcase, Clock, CheckCircle, Users, Target, ArrowLeft, Edit, Plus, Trash2, AlertCircle, Calendar, FileText, Workflow, UserPlus, X, Search, User } from 'lucide-react';
import { getProject, updateProject, deleteProject, getTeamContacts, getProjectTeamMembers, addTeamMemberToProject, removeTeamMemberFromProject } from '../../lib/supabase/projects';
import { getTasksForProject, createProjectTask } from '../../lib/supabase/tasks';
import { getContacts } from '../../lib/supabase/contacts';
import { useEntity } from '../../hooks/useEntity';
import type { Project } from '../../types/project';
import type { ProjectTask } from '../../types/project';
import type { Contact } from '../../types/contacts';
import { useAuthStore } from '../../store/authStore';
import ProjectForm from './ProjectForm';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';

interface ProjectDetailsProps {
  projectId: string;
  onBack: () => void;
}

export default function ProjectDetails({ projectId, onBack }: ProjectDetailsProps) {
  const { entity } = useEntity();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'team' | 'files'>('overview');
  const [showAddTask, setShowAddTask] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTeamMembers, setShowTeamMembers] = useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [teamMembers, setTeamMembers] = useState<Array<Contact>>([]);
  const [projectTeamMembers, setProjectTeamMembers] = useState<Array<any>>([]);
  const [teamContacts, setTeamContacts] = useState<Contact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [teamMemberRole, setTeamMemberRole] = useState<string>('');
  const [teamContactsLoading, setTeamContactsLoading] = useState(false);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [addingTeamMember, setAddingTeamMember] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    dueDate: ''
  });
  const { user } = useAuthStore();

  useEffect(() => {
    loadProject();
    loadTasks();
    loadTeamMembers();
  }, [projectId]);

  useEffect(() => {
    if (project) {
      loadTeamMembers();
      loadProjectTeamMembers();
    }
  }, [project]);

  useEffect(() => {
    if (entity?.id && showAddTeamMember) {
      loadTeamContacts();
    }
  }, [entity?.id, showAddTeamMember]);

  const loadProject = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProject(projectId);
      setProject(data);
    } catch (err) {
      console.error('Failed to load project:', err);
      setError('Failed to load project details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async () => {
    try {
      setTasksLoading(true);
      const data = await getTasksForProject(projectId);
      if (data) {
        setTasks(data);
      }
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  };

  const loadProjectTeamMembers = async () => {
    if (!projectId) return;
    
    try {
      const members = await getProjectTeamMembers(projectId);
      setProjectTeamMembers(members);
    } catch (error) {
      console.error('Failed to load project team members:', error);
    }
  };

  const loadTeamMembers = async () => {
    if (!entity?.id) return;
    
    try {
      setLoadingTeamMembers(true);
      setTeamMembersLoading(true);
      const contacts = await getContacts(entity.id);
      // Filter to only team contacts
      const teamContacts = contacts.filter(contact => contact.type === 'team');
      
      setTeamMembers(teamContacts);
      const members = await getProjectTeamMembers(projectId);
      setProjectTeamMembers(members);
    } catch (error) {
      console.error('Failed to load team members:', error);
    } finally {
      setLoadingTeamMembers(false);
      setTeamMembersLoading(false);
    }
  };

  const loadTeamContacts = async () => {
    if (!entity?.id) return;
    
    try {
      setTeamContactsLoading(true);
      const contacts = await getTeamContacts(entity.id);
      setTeamContacts(contacts);
    } catch (err) {
      console.error('Failed to load team contacts:', err);
    } finally {
      setTeamContactsLoading(false);
    }
  };

  const handleAddTeamMember = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedContactId && !selectedTeamMember) return;
    
    const contactId = selectedContactId || selectedTeamMember;
    if (!contactId || !projectId) return;
    
    try {
      setAddingTeamMember(true);
      
      await addTeamMemberToProject(projectId, contactId, teamMemberRole);
      
      // Refresh team members
      await loadProjectTeamMembers();
      
      // Reset form
      setSelectedContactId('');
      setSelectedTeamMember(null);
      setTeamMemberRole('');
      setShowAddTeamMember(false);
      
      // Update project to reflect new team size
      await loadProject();
    } catch (error) {
      console.error('Failed to add team member:', error);
      alert('Failed to add team member. Please try again.');
    } finally {
      setAddingTeamMember(false);
    }
  };

  const handleRemoveTeamMember = async (teamMemberId: string) => {
    try {
      await removeTeamMemberFromProject(teamMemberId);
      loadTeamMembers();
    } catch (error) {
      console.error('Failed to remove team member:', error);
    }
  };

  const filteredTeamMembers = teamMembers.filter(member => 
    `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.title && member.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredTeamContacts = teamContacts.filter(contact => {
    const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project || !user) return;
    
    try {
      await createProjectTask(
        project.id,
        {
          title: newTask.title,
          description: newTask.description,
          status: newTask.status,
          priority: newTask.priority,
          due_date: newTask.dueDate ? new Date(newTask.dueDate).toISOString() : undefined,
          created_by: user.id
        }
      );
      
      // Reset form and refresh tasks
      setNewTask({
        ...newTask,
        title: '',
        description: ''
      });
      setShowAddTask(false);
      loadTasks();
      
      // Update project progress
      loadProject();
    } catch (error) {
      console.error('Failed to add task:', error);
    }
  };

  const handleEditProject = () => {
    if (project) {
      setShowEditProject(true);
    }
  };

  const handleProjectUpdated = async () => {
    setShowEditProject(false);
    await loadProject();
  };

  const handleDeleteProject = async () => {
    if (!project) return;
    
    try {
      setIsDeleting(true);
      await deleteProject(project.id);
      onBack(); // Go back to project list after deletion
    } catch (error) {
      console.error('Failed to delete project:', error);
      setError('Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning':
        return 'bg-yellow-100 text-yellow-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      case 'review':
        return 'bg-purple-100 text-purple-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'on_hold':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress < 30) return 'bg-yellow-500';
    if (progress < 70) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'todo':
        return 'bg-gray-100 text-gray-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      case 'review':
        return 'bg-purple-100 text-purple-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTaskPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-gray-500">Loading project details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          onClick={loadProject}
          className="text-blue-500 hover:text-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Project not found</p>
        <button 
          onClick={onBack}
          className="text-blue-500 hover:text-blue-600 mt-4 flex items-center mx-auto"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button 
          onClick={onBack} 
          className="text-gray-500 hover:text-gray-700 flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Projects
        </button>
        
        <div className="flex space-x-2">
          <button 
            onClick={handleEditProject}
            className="text-blue-500 hover:text-blue-600 flex items-center"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit Project
          </button>
          <button 
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-500 hover:text-red-600 flex items-center"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-2 rounded-full">
                <Briefcase className="w-6 h-6 text-blue-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">{project.name}</h1>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(project.status)}`}>
                {project.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
            <p className="text-gray-600 mt-1">{project.description}</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {project.due_date && (
              <div className="text-sm text-gray-500">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>Due {new Date(project.due_date).toLocaleDateString()}</span>
                </div>
              </div>
            )}
            <div className="text-sm text-gray-500">
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-1 text-blue-500" />
                <span>{project.team_size || 0} members</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-500">Progress</span>
            <span className="text-sm font-medium text-gray-700">{project.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${getProgressColor(project.progress)}`}
              style={{ width: `${project.progress}%` }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            className={`px-4 py-3 text-sm font-medium ${
              activeTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`px-4 py-3 text-sm font-medium ${
              activeTab === 'tasks' 
                ? 'text-blue-600 border-b-2 border-blue-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('tasks')}
          >
            Tasks
          </button>
          <button
            className={`px-4 py-3 text-sm font-medium ${
              activeTab === 'team' 
                ? 'text-blue-600 border-b-2 border-blue-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('team')}
          >
            Team
          </button>
          <button
            className={`px-4 py-3 text-sm font-medium ${
              activeTab === 'files' 
                ? 'text-blue-600 border-b-2 border-blue-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('files')}
          >
            Files
          </button>
        </div>
        
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <div className="bg-white rounded-lg border border-gray-100 p-4">
                    <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                      <Target className="w-5 h-5 text-blue-500 mr-2" />
                      Project Vision
                    </h3>
                    <p className="text-gray-600">
                      {project.vision || 'No vision statement provided.'}
                    </p>
                  </div>
                </div>
                
                <div className="md:col-span-1">
                  <div className="bg-white rounded-lg border border-gray-100 p-4">
                    <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                      <Users className="w-5 h-5 text-indigo-500 mr-2" />
                      Team
                      <button 
                        onClick={() => setShowTeamMembers(true)}
                        className="ml-2 text-xs text-blue-500 hover:text-blue-600 flex items-center"
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        View Team
                      </button>
                    </h3>
                    <div className="flex items-center">
                      <span className="text-3xl font-bold text-gray-900">{project.team_size || 0}</span>
                      <span className="ml-2 text-gray-500">members</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <div className="bg-white rounded-lg border border-gray-100 p-4">
                    <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      Project Goals
                    </h3>
                    <p className="text-gray-600 whitespace-pre-line">
                      {project.goals || 'No goals specified.'}
                    </p>
                  </div>
                </div>
                
                <div className="md:col-span-1">
                  <div className="bg-white rounded-lg border border-gray-100 p-4">
                    <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                      <Calendar className="w-5 h-5 text-blue-500 mr-2" />
                      Timeline
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Created:</span>
                        <span className="text-gray-900">{new Date(project.created_at).toLocaleDateString()}</span>
                      </div>
                      {project.due_date && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Due:</span>
                          <span className="text-gray-900">{new Date(project.due_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Updated:</span>
                        <span className="text-gray-900">{new Date(project.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg border border-gray-100 p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                  <Target className="w-5 h-5 text-purple-500 mr-2" />
                  Project Workflow
                </h3>
                
                <div className="relative py-6">
                  {/* Workflow line */}
                  <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 transform -translate-y-1/2 z-0"></div>
                  
                  {/* Workflow steps */}
                  <div className="relative z-10 flex justify-between">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full ${project.status === 'planning' ? 'bg-blue-500' : (project.progress > 0 ? 'bg-green-500' : 'bg-gray-200')} flex items-center justify-center mb-2`}>
                        {project.progress > 0 ? (
                          <CheckCircle className="w-6 h-6 text-white" />
                        ) : (
                          <Target className="w-6 h-6 text-white" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900">Planning</span>
                    </div>
                    
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full ${project.status === 'in_progress' ? 'bg-blue-500' : (project.progress >= 25 ? 'bg-green-500' : 'bg-gray-200')} flex items-center justify-center mb-2`}>
                        {project.progress >= 25 ? (
                          <CheckCircle className="w-6 h-6 text-white" />
                        ) : (
                          <Target className="w-6 h-6 text-white" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900">In Progress</span>
                    </div>
                    
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full ${project.status === 'review' ? 'bg-blue-500' : (project.progress >= 75 ? 'bg-green-500' : 'bg-gray-200')} flex items-center justify-center mb-2`}>
                        {project.progress >= 75 ? (
                          <CheckCircle className="w-6 h-6 text-white" />
                        ) : (
                          <Target className="w-6 h-6 text-white" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900">Review</span>
                    </div>
                    
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full ${project.status === 'completed' ? 'bg-green-500' : 'bg-gray-200'} flex items-center justify-center mb-2`}>
                        {project.status === 'completed' ? (
                          <CheckCircle className="w-6 h-6 text-white" />
                        ) : (
                          <Target className="w-6 h-6 text-white" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900">Completed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'tasks' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium text-gray-900">Project Tasks</h3>
                <button 
                  onClick={() => setShowAddTask(true)}
                  className="text-blue-500 hover:text-blue-600 text-sm flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Task
                </button>
              </div>
              
              {showAddTask && (
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-gray-900 mb-3">Add New Task</h4>
                  <form onSubmit={handleAddTask} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Task Title
                      </label>
                      <input
                        type="text"
                        value={newTask.title}
                        onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={newTask.description}
                        onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <select
                          value={newTask.status}
                          onChange={(e) => setNewTask(prev => ({ ...prev, status: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        >
                          <option value="todo">To Do</option>
                          <option value="in_progress">In Progress</option>
                          <option value="review">Review</option>
                          <option value="completed">Done</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority
                        </label>
                        <select
                          value={newTask.priority}
                          onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Due Date
                        </label>
                        <input
                          type="date"
                          value={newTask.dueDate}
                          onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowAddTask(false)}
                        className="px-3 py-1.5 text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600"
                      >
                        Add Task
                      </button>
                    </div>
                  </form>
                </div>
              )}
              
              {tasksLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-gray-500 text-sm mt-2">Loading tasks...</p>
                </div>
              ) : tasks.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No tasks yet. Add your first task to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-500 px-4 py-2">
                    <span className="w-1/3">Task</span>
                    <div className="flex w-2/3">
                      <span className="w-1/4">Status</span>
                      <span className="w-1/4">Priority</span>
                      <span className="w-1/4">Due Date</span>
                      <span className="w-1/4">Operations</span>
                    </div>
                  </div>
                  
                  {tasks.map(task => (
                    <div key={task.id} className="flex justify-between items-center bg-white border border-gray-100 rounded-lg p-4 hover:shadow-sm transition-shadow">
                      <div className="w-1/3">
                        <p className="font-medium text-gray-900">{task.title || 'Untitled Task'}</p>
                        {task.description && (
                          <p className="text-sm text-gray-500 truncate">{task.description}</p>
                        )}
                      </div>
                      <div className="flex w-2/3">
                        <div className="w-1/4">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${getTaskStatusColor(task.status)}`}>
                            {task.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                        <div className="w-1/4">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${getTaskPriorityColor(task.priority)}`}>
                            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                          </span>
                        </div>
                        <div className="w-1/4">
                          {task.due_date ? (
                            <span className="text-sm text-gray-500">
                              {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </div>
                        <div className="w-1/4 flex items-center">
                          <button 
                            className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'team' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-medium text-gray-900 flex items-center space-x-2">
                  <Users className="w-5 h-5 text-blue-500 mr-2" />
                  <span>Project Team</span>
                </h3>
                <button 
                  onClick={() => setShowAddTeamMember(true)}
                  className="bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 text-sm flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Team Member
                </button>
              </div>
              
              {teamMembersLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading team members...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projectTeamMembers.length > 0 ? (
                    projectTeamMembers.map((member) => (
                      <div key={member.id} className="bg-white rounded-lg border border-gray-100 p-4 flex items-start space-x-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-medium">
                          {member.contact.first_name.charAt(0)}{member.contact.last_name.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <h4 className="font-medium text-gray-900">{member.contact.first_name} {member.contact.last_name}</h4>
                            <button 
                              onClick={() => handleRemoveTeamMember(member.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          {member.role && (
                            <p className="text-sm text-gray-500">{member.role}</p>
                          )}
                          {member.contact.title && (
                            <p className="text-xs text-gray-400">{member.contact.title}</p>
                          )}
                          {member.contact.emails && member.contact.emails.length > 0 && (
                            <div className="mt-1 text-xs text-blue-500">
                              {member.contact.emails[0].email}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8 bg-gray-50 rounded-lg">
                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No team members assigned yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'files' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium text-gray-900 flex items-center">
                  <FileText className="w-5 h-5 text-blue-500 mr-2" />
                  Project Files
                </h3>
                <button className="bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 text-sm flex items-center">
                  <Plus className="w-4 h-4 mr-1" />
                  Upload File
                </button>
              </div>
              
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No files uploaded yet.</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {showEditProject && project && (
        <ProjectForm
          project={project}
          onClose={() => setShowEditProject(false)}
          onSave={handleProjectUpdated}
        />
      )}
      
      {showAddTeamMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Team Member</h3>
              <button 
                onClick={() => setShowAddTeamMember(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {teamContactsLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-gray-500 text-sm mt-2">Loading team contacts...</p>
              </div>
            ) : teamContacts.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500">No team contacts found. Please add team contacts in the Contacts section first.</p>
              </div>
            ) : (
              <form onSubmit={handleAddTeamMember} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Team Members
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="Search by name..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Team Member
                  </label>
                  <select
                    value={selectedContactId}
                    onChange={(e) => setSelectedContactId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    required
                  >
                    <option value="">Select a team member</option>
                    {filteredTeamContacts.map(contact => (
                      <option key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name} {contact.title ? `(${contact.title})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role in Project (Optional)
                  </label>
                  <input
                    type="text"
                    value={teamMemberRole}
                    onChange={(e) => setTeamMemberRole(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="e.g., Project Manager, Developer, Designer"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddTeamMember(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedContactId || addingTeamMember}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center"
                  >
                    {addingTeamMember ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Adding...
                      </>
                    ) : (
                      <>Add to Team</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      
      {showDeleteConfirm && (
        <DeleteConfirmationModal
          title="Delete Project"
          message="Are you sure you want to delete this project? This action cannot be undone."
          isDeleting={isDeleting}
          onConfirm={handleDeleteProject}
          onCancel={() => setShowDeleteConfirm(false)}
          itemName={project.name}
        />
      )}
      
      {/* Team Members Modal */}
      {showTeamMembers && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Project Team</h2>
              <button onClick={() => setShowTeamMembers(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 relative">
              <input
                type="text"
                placeholder="Search team members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
            
            {loadingTeamMembers ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No team members found</p>
                <p className="text-sm text-gray-400 mt-1">Add team contacts in the Operations section</p>
                <button 
                  onClick={() => {
                    setShowTeamMembers(false);
                    // Navigate to contacts page
                    window.location.href = '/dashboard?section=operations&area=contacts';
                  }}
                  className="mt-4 text-blue-500 hover:text-blue-600 text-sm"
                >
                  Go to Contacts
                </button>
              </div>
            ) : (
              <div className="space-y-4 max-h-60 overflow-y-auto">
                {filteredTeamMembers.map(member => (
                  <div key={member.id} className="flex items-center space-x-3 p-3 border border-gray-100 rounded-lg">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-500 font-medium">
                      {`${member.first_name[0]}${member.last_name[0]}`.toUpperCase()}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{`${member.first_name} ${member.last_name}`}</h4>
                      {member.title && <p className="text-sm text-gray-500">{member.title}</p>}
                    </div>
                  </div>
                ))}
                {filteredTeamMembers.length === 0 && <p className="text-center text-gray-500">No matching team members found</p>}
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowTeamMembers(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Team Member Modal */}
      {showAddTeamMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Add Team Member</h2>
              <button onClick={() => setShowAddTeamMember(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Team Members
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search team members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                />
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Team Member
              </label>
              
              {loadingTeamMembers ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="text-center py-4 bg-gray-50 rounded-lg">
                  <User className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No team members found</p>
                  <p className="text-sm text-gray-400 mt-1">Add team contacts in the Operations section</p>
                </div>
              ) : (
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredTeamMembers.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500">No matching team members found</p>
                    </div>
                  ) : (
                    filteredTeamMembers.map(member => {
                      // Check if this member is already in the project
                      const isAlreadyInProject = projectTeamMembers.some(
                        ptm => ptm.contact_id === member.id
                      );
                      
                      return (
                        <div 
                          key={member.id} 
                          className={`flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer ${
                            selectedTeamMember === member.id ? 'bg-blue-50' : ''
                          } ${isAlreadyInProject ? 'opacity-50' : ''}`}
                          onClick={() => {
                            if (!isAlreadyInProject) {
                              setSelectedTeamMember(member.id);
                            }
                          }}
                        >
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-500 font-medium mr-3">
                              {`${member.first_name[0]}${member.last_name[0]}`.toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{`${member.first_name} ${member.last_name}`}</p>
                              {member.title && <p className="text-sm text-gray-500">{member.title}</p>}
                            </div>
                          </div>
                          
                          {isAlreadyInProject && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                              Already added
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role (Optional)
              </label>
              <input
                type="text"
                value={teamMemberRole}
                onChange={(e) => setTeamMemberRole(e.target.value)}
                placeholder="e.g., Project Manager, Designer, Developer"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAddTeamMember(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={addingTeamMember}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddTeamMember}
                disabled={!selectedTeamMember || addingTeamMember || teamMembers.length === 0}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center"
              >
                {addingTeamMember ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add to Project
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}